/**
 * Executive-facing risk driver copy for Risk Overview (and similar rollups).
 * Never surfaces raw engine strings (index / % / "higher = …") in the UI.
 */

const { finalizeExplanation, simplifyCopy } = require('@stavyn/shared/simpleCopy');

const RAW_ANALYTICS_RE =
  /index\s*\d|higher\s*=\s*|subject\s|peer\s+avg\s*\d|vs\s+previous\s+period|teamattritiontrend|external\s+role\s+demand\s+index|rate\s+moved|mismatch\s+index/i;

/**
 * @param {string} line
 * @returns {{ title: string, sentence: string }}
 */
export function humanizeRiskReasonOverview(line) {
  const raw = String(line || '').trim();
  const l = raw.toLowerCase();

  if (!raw) {
    return { title: 'Retention signal', sentence: 'Review profile details for a fuller picture.' };
  }

  // Most specific patterns first
  if (/absenteeism|absence pattern|rate moved.*vs previous|vs previous period/.test(l)) {
    return {
      title: 'Attendance change',
      sentence: 'Recent attendance changes may signal strain or disengagement.',
    };
  }
  if (/external\s+role\s+demand|external\s+demand.*index|demand\s+index.*external|outside\s+demand\s+for/.test(l)) {
    return {
      title: 'External demand',
      sentence: 'This role is in strong outside demand. That makes it easier to leave.',
    };
  }
  if (/performance.{0,20}reward|reward.{0,12}mismatch|recognition.{0,20}mismatch|performance.{0,16}mismatch/.test(l)) {
    return {
      title: 'Strong performer, not recognised',
      sentence: 'Good work is not reflected in pay or advancement. That often leads to departure.',
    };
  }
  if (/promot|peer avg/.test(l)) {
    return {
      title: 'No recent promotion',
      sentence: 'Advancement has lagged behind peers. That often leads people to look elsewhere.',
    };
  }
  if (/skill transferab/.test(l)) {
    return {
      title: 'High-demand skills',
      sentence: 'Their skills transfer easily to other employers.',
    };
  }
  if (/teamattritiontrend|team attrition|worsening.*attrition/.test(l)) {
    return {
      title: 'Team losing people',
      sentence: 'Recent team departures make others more likely to leave too.',
    };
  }
  if (/tenure/.test(l) && !/manager.{0,12}tenure|tenure.{0,8}manager/.test(l)) {
    return {
      title: 'Long time without growth',
      sentence: 'Long time in the same role without advancement increases risk of leaving.',
    };
  }
  if (/market pull|market demand|competitive in the market|inbound recruiting|outside opportunities/.test(l)) {
    return {
      title: 'Market pull',
      sentence: 'Market conditions increase the chance of outside offers.',
    };
  }
  if (/manager change|manager churn|manager transitions|manager continuity/.test(l)) {
    return {
      title: 'Manager continuity',
      sentence: 'Leadership changes can disrupt clarity and trust.',
    };
  }
  if (/below typical peers|below market|pay is below|compensation lag|comp gap|salary vs|under-?market/.test(l)) {
    return {
      title: 'Paid below market',
      sentence: 'Pay trails similar roles. Outside offers may be easier to accept.',
    };
  }
  if (/workload|overload|burnout/.test(l)) {
    return {
      title: 'Too much workload',
      sentence: 'Sustained pressure is a strong sign someone may leave.',
    };
  }
  if (/productivity/.test(l)) {
    return {
      title: 'Output dropping',
      sentence: 'Output is falling. That often signals rising disengagement.',
    };
  }
  if (/collaboration/.test(l)) {
    return {
      title: 'Collaboration',
      sentence: 'Collaboration has softened. That can be an early warning sign.',
    };
  }

  if (RAW_ANALYTICS_RE.test(raw)) {
    return {
      title: 'Retention signal',
      sentence: 'Review this profile for more detail.',
    };
  }

  // Short, non-jargony passthrough: still give a title
  if (raw.length <= 120 && !/\d{2,}\.\d/.test(raw)) {
    return { title: 'Context', sentence: finalizeExplanation(raw) };
  }

  return {
    title: 'Retention signal',
    sentence: 'Review this profile for more detail.',
  };
}

function driverKey(r) {
  return (r.title || '').toLowerCase().trim();
}

/**
 * @param {string[]} riskReasons
 * @param {number} [max]
 * @returns {{ title: string, sentence: string }[]}
 */
export function selectOverviewDrivers(riskReasons, max = 3) {
  const seen = new Set();
  const out = [];
  for (const line of riskReasons || []) {
    const r = humanizeRiskReasonOverview(line);
    const k = driverKey(r);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push({
      title: r.title,
      sentence: simplifyCopy(r.sentence),
    });
    if (out.length >= max) break;
  }
  return out;
}
